import { ObservedAttributes } from "@self/utils";
import { bound, customElement } from "./CustomElement";
import { makeStylesheetLink } from "@self/stylesheet";

export type ScrollDir =
	| "left"
	| "right"
	;

export type ScrollDirAttr =
	| ScrollDir
	| (string & {})
	| null
	;

const SCROLL_ATTR_NAME = "scroll-dir";

const prev = "<svg width=\"24\" height=\"24\" viewBox=\"0 0 24 24\"><path d=\"M8.64602 11.7L14.2998 6.04625L15.3535 7.1L10.7535 11.7L15.3535 16.3L14.2998 17.3538L8.64602 11.7Z\" fill=\"currentColor\"/></svg>";
const next = "<svg width=\"24\" height=\"24\" viewBox=\"0 0 24 24\"><path d=\"M15.354 11.7L9.70024 17.3538L8.64648 16.3L13.2465 11.7L8.64648 7.10001L9.70024 6.04626L15.354 11.7Z\" fill=\"currentColor\"/></svg>";

@customElement("gct-scroll")
export class Scroll extends HTMLElement {

	static get observedAttributes() {
		return [SCROLL_ATTR_NAME, "disabled", "for"] as const;
	}

	shadow: ShadowRoot;
	button: HTMLButtonElement;
	container: HTMLElement | null;
	ro?: ResizeObserver;

	get scrollDir(): ScrollDirAttr {
		return this.getAttribute(SCROLL_ATTR_NAME) as ScrollDirAttr;
	}

	set scrollDir(value: ScrollDirAttr) {
		if (value !== null) {
			this.setAttribute(SCROLL_ATTR_NAME, value);
		} else {
			this.removeAttribute(SCROLL_ATTR_NAME);
		}
	}

	get disabled() { return this.hasAttribute("disabled"); }

	set disabled(value: boolean) {
		if (value) {
			this.setAttribute("disabled", "");
		} else {
			this.removeAttribute("disabled");
		}
	}

	get for() { return this.getAttribute("for"); }

	set for(value: string | null) {
		if (value !== null) {
			this.setAttribute("for", value);
		} else {
			this.removeAttribute("for");
		}
	}

	constructor() {
		super();

		this.button = <button type="button" /> as HTMLButtonElement;
		this.container = null;

		this.button.addEventListener("click", this.onClick);

		this.updateScrollDir(this.scrollDir);

		this.shadow = this.attachShadow({ mode: "open" });
		this.shadow.appendChild(makeStylesheetLink("gct-scroll.css"));
		this.shadow.appendChild(this.button);
	}

	connectedCallback() {
		this.updateFor(this.for);
		requestAnimationFrame(() => this.updateDisabled(this.disabled));

		window.addEventListener("load", this.onScroll, { passive: true });
    window.addEventListener("resize", this.onScroll, { passive: true });
	}

	disconnectedCallback() {
    this.container?.removeEventListener("scroll", this.onScroll as any);
    this.ro?.disconnect();
    this.ro = undefined;

    window.removeEventListener("load", this.onScroll as any);
    window.removeEventListener("resize", this.onScroll as any);

    this.container = null;
  }
	
	attributeChangedCallback(name: ObservedAttributes<typeof Scroll>, _oldValue: string | null, newValue: string | null) {
		switch (name) {
			case SCROLL_ATTR_NAME:
				this.updateScrollDir(newValue);
				break;
			case "disabled":
				this.updateDisabled(newValue !== null);
				break;
			case "for":
				this.updateFor(newValue);
				break;
		}
	}

	@bound
	onClick() {
		const { container, scrollDir } = this;
		if (container === null || (scrollDir !== "left" && scrollDir !== "right")) return;

		const secondChild = container.children[1] as HTMLElement | undefined;
		const step = secondChild?.offsetLeft;
		if (!step) return;

		container.scrollBy({
			left: scrollDir === "left" ? -step : step,
			behavior: "smooth",
		});
	}

	@bound
	onScroll() {
		this.updateDisabled(this.disabled);
	}

	private updateScrollDir(scrollDir: ScrollDirAttr) {
		switch (scrollDir) {
			case "left":
				this.button.innerHTML = prev;
				break;
			case "right":
				this.button.innerHTML = next;
				break;
			default:
				this.button.innerHTML = "";
				break;
		}
	}

	private updateDisabled(disabled: boolean) {
    if (disabled || this.container === null) {
      this.button.disabled = true;
      return;
    }

    const c = this.container;
    const epsilon = 1;

    switch (this.scrollDir) {
      case "left": {
        this.button.disabled = c.scrollLeft <= 0 + epsilon;
        break;
      }
      case "right": {
        const endReached = c.scrollLeft + c.clientWidth >= c.scrollWidth - epsilon;
        this.button.disabled = endReached;
        break;
      }
      default:
        this.button.disabled = true;
        break;
    }
  }

	private updateFor(htmlFor: string | null) {
    this.container?.removeEventListener("scroll", this.onScroll as any);
    this.ro?.disconnect();
    this.ro = undefined;

    this.container = htmlFor !== null
      ? document.getElementById(htmlFor)
      : this.parentElement;

    if (this.container) {
      this.container.addEventListener("scroll", this.onScroll, { passive: true });

      this.ro = new ResizeObserver(() => this.onScroll());
      this.ro.observe(this.container);

      this.updateDisabled(this.disabled);
    }
	}
}
